import Card from '../ui/Card';
import ProgressBar from '../ui/ProgressBar';
import { Skeleton } from '../Skeleton';
import {
    listReports,
    MODULE_LABELS,
    MODULE_ORDER,
    type Report,
    type ModuleKey,
} from '../../mentorshipService';
import { useFetchOnMount } from './useFetchOnMount';
import './ProgressionTimeline.css';
import './mentorship.css';

type Props = {
    menteeId: number;
};

type ReportsByModule = Map<ModuleKey, Report[]>;

function ProgressionTimeline({ menteeId }: Props) {
    const { data: reports, loading } = useFetchOnMount<Report[]>(
        async () => (await listReports({ mentee_id: menteeId })).results,
        [menteeId],
    );

    if (loading) {
        return (
            <div className="progression-timeline__loading" aria-busy="true">
                <Skeleton width="100%" height="80px" borderRadius="12px" />
                <Skeleton width="100%" height="80px" borderRadius="12px" />
            </div>
        );
    }

    if (!reports || reports.length === 0) {
        return (
            <div className="progression-timeline__empty">
                <h3>No history yet</h3>
                <p>
                    Once your mentor finalizes your first report, your progression
                    across each module will appear here as a timeline.
                </p>
            </div>
        );
    }

    // Group by module, then sort each module's reports oldest-first
    const byModule: ReportsByModule = new Map();
    for (const r of reports) {
        const m = r.submission_summary.module;
        const list = byModule.get(m) ?? [];
        list.push(r);
        byModule.set(m, list);
    }
    for (const list of byModule.values()) {
        list.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    }

    return (
        <div className="progression-timeline">
            {MODULE_ORDER
                .filter((m) => byModule.has(m))
                .map((module) => {
                    const moduleReports = byModule.get(module) ?? [];
                    const latest = moduleReports[moduleReports.length - 1];
                    const latestPercent = (latest.final_score / 5) * 100;

                    return (
                        <Card variant="row" key={module} className="progression-timeline__row">
                            <Card.Header>
                                <div className="progression-timeline__module-info">
                                    <span className="mentorship-chip mentorship-chip--module">
                                        {MODULE_LABELS[module]}
                                    </span>
                                    <span
                                        className={`mentorship-chip mentorship-chip--level-${latest.agreed_level.toLowerCase()}`}
                                    >
                                        {latest.agreed_level}
                                    </span>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <div className="progression-timeline__score-row">
                                    <span className="progression-timeline__current">
                                        Latest: {latest.final_score}/5
                                    </span>
                                    <span className="progression-timeline__quarters">
                                        {moduleReports.length}{' '}
                                        {moduleReports.length === 1 ? 'review' : 'reviews'}
                                    </span>
                                </div>
                                <ProgressBar
                                    value={latestPercent}
                                    ariaLabel={`${MODULE_LABELS[module]} latest score ${latest.final_score} of 5`}
                                />
                                <div className="progression-timeline__history">
                                    {moduleReports.map((r) => (
                                        <span
                                            key={r.id}
                                            className="progression-timeline__pill"
                                            title={`v${r.version} — ${r.submission_summary.quarter} — ${r.final_score}/5`}
                                        >
                                            {r.submission_summary.quarter}: {r.final_score}
                                        </span>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    );
                })}
        </div>
    );
}

export default ProgressionTimeline;
